import React from "react";
import { DeveloperCard } from "@/components/DeveloperCard";
import { AboutSettings } from "@/components/AboutSettings";
import { LegalSettings } from "@/components/Legal";
import { AboutLink } from "@/pages/About";
import { DevicesCard } from "@/components/DevicesCard";
import { Sliders, Clock } from "lucide-react";
import { PageTitle, SettingsPanel } from "@/components/SettingsPanel";
import { TeamManager } from "@/components/TeamManager";
import { LaunchExemption } from "@/components/LaunchExemption";
import { BalanceReminderSettings } from "@/components/BalanceReminderSettings";
import { PlatformContactSettings } from "@/components/PlatformContact";
import { PushToggle } from "@/components/PushToggle";
import { RejectReasonsSettings } from "@/components/RejectReasonsSettings";
import { ImageLimitsSettings } from "@/components/ImageLimitsSettings";
import { OnboardingSettings } from "@/components/OnboardingSettings";
import { HeroSettings } from "@/components/HeroSettings";
import { CustomerSectionsSettings } from "@/components/CustomerSectionsSettings";
import { usePrefs } from "@/contexts/PrefsContext";
import { useAuth } from "@/contexts/AuthContext";
import type { User } from "@/types";

const CFG = { base: "/admin/team", roleKey: "admin_role" as const, roles: ["super", "reception", "finance"], prefix: "admin" };

export default function AdminSettings() {
  const { t } = usePrefs();
  const me = useAuth().user as User;
  const isSuper = (me.admin_role || "super") === "super";
  return (
    <div data-testid="admin-settings-page" className="space-y-10">
      <div>
        <PageTitle title={t("settings.title")} />
        <SettingsPanel>
          <DevicesCard />
          <DeveloperCard />
          <AboutLink className="px-2" />
          <div className="rounded-3xl border bg-card p-5 flex gap-3 items-center" data-testid="timezone-card">
            <Clock className="h-5 w-5 text-primary shrink-0" />
            <div>
              <p className="font-bold">{t("settings.timezone")}</p>
              <p className="text-sm text-muted-foreground">{t("settings.tzValue")}</p>
            </div>
          </div>
          <PushToggle />
          {isSuper && <CustomerSectionsSettings />}
          {isSuper && <PlatformContactSettings />}
          {isSuper && <AboutSettings />}
          {isSuper && <LegalSettings />}
          {isSuper && <RejectReasonsSettings />}
          <HeroSettings canEdit={isSuper} />
          <OnboardingSettings canEdit={isSuper} />
          <ImageLimitsSettings canEdit={isSuper} />
          <LaunchExemption canEdit={isSuper} />
          {["super", "finance"].includes(me.admin_role || "super") && <BalanceReminderSettings />}
          <div className="rounded-3xl border border-dashed bg-card p-5 flex gap-3 items-start">
            <Sliders className="h-5 w-5 text-secondary shrink-0 mt-0.5" />
            <div>
              <p className="font-bold">{t("settings.platform")}</p>
              <p className="text-sm text-muted-foreground">{t("settings.platformSoon")}</p>
            </div>
          </div>
        </SettingsPanel>
      </div>
      {isSuper && (
        <section data-testid="admin-team-section">
          <h2 className="font-display text-xl font-black mb-4">{t("admin.team")}</h2>
          <TeamManager cfg={CFG} canManage />
        </section>
      )}
    </div>
  );
}
